import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { Ear, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [employeeId, setEmployeeId] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  useEffect(() => {
    if (user && user.id) {
      navigate(from, { replace: true });
    }
  }, [user, from, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(employeeId.trim(), password);
      toast.success("Sesión iniciada");
      navigate(from, { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-[#F7F6F3]">
      {/* Visual side */}
      <div className="hidden md:flex md:w-1/2 relative overflow-hidden">
        <img
          src="https://images.pexels.com/photos/19921278/pexels-photo-19921278.jpeg"
          alt="Sala de espera del centro auditivo"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D332F]/90 via-[#2D332F]/70 to-[#4A5D4E]/60" />
        <div className="relative z-10 p-12 flex flex-col items-center justify-center text-white w-full text-center">
          <img
            src="/img/gestionaudio-logo-transparent.png"
            alt="GestiónAudio"
            className="h-56 w-auto object-contain mb-8 drop-shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
            style={{ filter: "brightness(0) invert(1)" }}
          />
          <div className="space-y-4 max-w-md">
            <div className="text-xs tracking-[0.2em] uppercase text-white/70">
              Gestión de centros auditivos
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-medium leading-[1.05] tracking-tight">
              Pacientes, audífonos y facturación en un solo sitio.
            </h1>
            <p className="text-white/80 text-base leading-relaxed">
              Controla varias clínicas desde un panel intuitivo. Fichas
              completas, anamnesis, stock por centro y facturas en PDF con
              IVA configurable.
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-white/70 mt-8">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
            Acceso seguro con identificación de trabajador
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 md:py-0">
        <div className="w-full max-w-sm">
          <div className="md:hidden mb-8 flex items-center justify-center">
            <img
              src="/img/gestionaudio-logo-transparent.png"
              alt="GestiónAudio"
              className="h-28 w-auto object-contain"
            />
          </div>

          <div className="text-xs tracking-[0.2em] uppercase text-[#6E7772] font-semibold">
            Iniciar sesión
          </div>
          <h2 className="font-heading text-3xl md:text-4xl tracking-tight font-medium text-[#2A2F2C] mt-2">
            Acceso al sistema
          </h2>
          <p className="text-[#6E7772] mt-2 text-sm">
            Usa tu ID de trabajador y contraseña.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="employee_id" className="text-xs tracking-wider uppercase text-[#6E7772] font-semibold">
                ID de trabajador
              </Label>
              <Input
                id="employee_id"
                data-testid="login-employee-id"
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                placeholder="ej. admin"
                autoComplete="username"
                required
                className="bg-white border-[#E5E3DB] h-11 focus-visible:ring-[#4A5D4E]/30 focus-visible:border-[#4A5D4E]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-xs tracking-wider uppercase text-[#6E7772] font-semibold">
                Contraseña
              </Label>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="bg-white border-[#E5E3DB] h-11 focus-visible:ring-[#4A5D4E]/30 focus-visible:border-[#4A5D4E]"
              />
            </div>

            {error && (
              <div
                className="text-sm bg-[#E8D1D1] text-[#2A2F2C] border border-[#D5B5B5] rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="w-full h-11 bg-[#4A5D4E] hover:bg-[#3C4A3F] text-white font-medium"
            >
              {loading ? "Entrando…" : "Iniciar sesión"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
